import React, {useEffect, useState} from "react";
import {deleteAsset, getAssetsForWorkshop, getAssetStatsForWorkshop} from "../../Api/Api";
import {useWorkshop} from "../Common/WorkshopContext";
import {FaChartPie, FaPlus} from "react-icons/fa";
import Pagination from "../Pagination/Pagination";
import AssetModal from "./AssetModal";
import {useNotification} from "../Notification/NotificationContext";
import ConfirmationModal from "../Inventory/DeleteInventoryModal";
import {Bar} from "react-chartjs-2";

const AssetPage = () => {
    const { workshopId } = useWorkshop();
    const { showNotification } = useNotification();

    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [filterType, setFilterType] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;
    const [showConfirmDelete, setShowConfirmDDelete] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);

    const [assetStatsData, setAssetStatsData] = useState(null);
    const assetChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => `Total: ${context.parsed.y}`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { stepSize: 1 }
            }
        }
    };


    const fetchStats = async () => {
        try {
            const response = await getAssetStatsForWorkshop(workshopId);

            // 1. Add this check: Ensure data exists and is an object
            const data = response?.data;

            if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
                console.warn("No asset stats data received");
                setAssetStatsData(null); // Clear chart if no data
                return;
            }

            // 2. Now it's safe to map
            const labels = Object.keys(data)?.map(key => key.charAt(0) + key.slice(1).toLowerCase());
            const counts = Object.values(data);

            setAssetStatsData({
                labels: labels,
                datasets: [{
                    label: "Asset Count",
                    data: counts,
                    backgroundColor: ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'],
                    borderColor: 'rgba(0,0,0,0.1)',
                    borderWidth: 1
                }]
            });
        } catch (error) {
            console.error("Error fetching asset stats", error);
            showNotification("Failed to load chart data", "error");
        }
    };

        useEffect(() => {
            fetchAssets();
            fetchStats();
        }, [workshopId, currentPage, filterType]);

        // Same options style as your FinanceReports

    const fetchAssets = async () => {
        setLoading(true);
        try {
            const res = await getAssetsForWorkshop(workshopId, pageSize, currentPage, filterType);
            setAssets(res.data.content);
            setTotalPages(res.data.totalPages);
            setTotalCount(res.data.totalElements);
        } catch (err) {
            showNotification(err.message || "Error fetching assets", "error");
        }
        setLoading(false);
    };

    const handleDeleteClick = (id) => {
        setSelectedItemId(id);
        setShowConfirmDDelete(true); // Show the confirmation modal
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteAsset(selectedItemId) // Call the API
            setShowConfirmDDelete(false); // Close the modal
            setSelectedItemId(null);
            fetchAssets();
        } catch (error) {
            showNotification(error.message, "error")
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmDDelete(false); // Close the modal
        setSelectedItemId(null);
    };


    useEffect(() => {
        fetchAssets();
        fetchStats();
    }, [workshopId, currentPage, filterType]);


    return (
        <div className="mt-6 max-w-screen-xl mx-auto">
            <div className="bg-white shadow-lg rounded-lg">
                <div className="bg-teal-400 text-white p-4 flex flex-col sm:flex-row justify-between items-center gap-4 rounded-t-lg">
                    <h2 className="text-xl font-semibold">Assets</h2>
                    <div className="flex gap-2 items-center w-full sm:w-auto">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-4 py-2 rounded-md text-gray-700"
                        >
                            <option value="">All Types</option>
                            <option value="VEHICLE">Vehicle</option>
                            <option value="BATTERY">Battery</option>
                            <option value="CHARGER">Charger</option>
                            <option value="MOTOR">Motor</option>
                            <option value="CONTROLLER">Controller</option>
                        </select>
                        <button
                            onClick={() => setShowModal(true)}
                            className="bg-white text-teal-500 px-4 py-2 rounded-md flex items-center hover:bg-gray-200"
                        >
                            <FaPlus className="mr-2" />
                            Add Asset
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center items-center space-x-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto shadow-md rounded-lg">
                                <table className="min-w-full bg-white">
                                    <thead className="bg-gray-50 text-gray-800">
                                    <tr>
                                        <th className="p-3 text-left">Asset Type</th>
                                        <th className="p-3 text-left">Serial/Chassis</th>
                                        <th className="p-3 text-left">Type</th>
                                        <th className="p-3 text-left">Assigned</th>
                                        <th className="p-3 text-left">Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {assets.map((asset) => (
                                        <tr key={asset.id} className="border-b hover:bg-gray-100">
                                            <td className="p-3 capitalize">{asset.assetType}</td>
                                            <td className="p-3">
                                                {asset.serialNumber}
                                            </td>
                                            <td className="p-3">{asset.type}</td>
                                            <td className="p-3">
                                                {asset.assigned && (
                                                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset">
    Yes
  </span>
                                                )}
                                            </td>

                                            <td  className="p-3">
                                                <button
                                                    onClick={() => handleDeleteClick(asset.id)}
                                                    className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition"
                                                >
                                                    Delete
                                                </button>

                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex justify-start mt-2">
                                <Pagination
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    currentPage={currentPage}
                                    totalCount={totalCount}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <br/>
            <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-teal-500 to-blue-500 text-white p-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <FaChartPie /> Asset Distribution by Type
                    </h3>
                </div>
                <div className="p-6">
                    {assetStatsData ? (
                        <div className="h-80">
                            <Bar data={assetStatsData} options={assetChartOptions} />
                        </div>
                    ) : (
                        <div className="h-80 flex items-center justify-center text-gray-400">
                            <p>Loading asset statistics...</p>
                        </div>
                    )}
                </div>
            </div>
            <AssetModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                onAssetAdded={fetchAssets}
            />
            <ConfirmationModal
                show={showConfirmDelete}
                message="Are you sure you want to delete this  item? This action cannot be undone."
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default AssetPage;
